import { useEffect, useState } from 'react';

interface IdleTimeoutState {
  showWarning: boolean;
  minutesLeft: number;
  secondsLeft: number;
}

interface IdleTimeoutActions {
  extendSession: () => void;
  dismissWarning: () => void;
}

// Simple, non-intrusive idle timeout - only checks every 5 minutes
export function useSimpleIdleTimeout(user?: { id: string } | null): IdleTimeoutState & IdleTimeoutActions {
  const [state, setState] = useState<IdleTimeoutState>({
    showWarning: false,
    minutesLeft: 5,
    secondsLeft: 300,
  });

  const extendSession = async () => {
    try {
      const response = await fetch('/api/extend-session', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        console.log('✅ Session extended successfully');
        setState(prev => ({ ...prev, showWarning: false }));
      } else {
        console.error('❌ Failed to extend session');
      }
    } catch (error) {
      console.error('❌ Session extension error:', error);
    }
  };

  const dismissWarning = () => {
    // Don't allow dismissing the warning - user must either extend or logout
    console.log('⚠️ Warning dismissal blocked - user must choose extend or logout');
  };

  useEffect(() => {
    // Only run for authenticated users
    if (!user?.id) {
      return;
    }

    // Create AbortController to prevent memory leaks on component unmount
    const controller = new AbortController();

    // Check timeout frequently for testing
    const checkTimeout = async () => {
      try {
        const response = await fetch('/api/check-timeout', {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal, // Add abortion support
        });

        if (response.ok) {
          const data = await response.json() as {
            shouldLogout: boolean;
            isNearTimeout: boolean;
            minutesLeft?: number;
            secondsLeft?: number;
          };

          if (data.shouldLogout) {
            console.log('🔴 Server says logout - redirecting to login');
            // Capture current URL for redirect after login
            const currentPath = window.location.pathname + window.location.search;
            const redirectUrl = currentPath !== '/' ? `/login?redirectTo=${encodeURIComponent(currentPath)}&message=session-expired` : '/login?message=session-expired';
            window.location.href = redirectUrl;
            return;
          }

          if (data.isNearTimeout && data.minutesLeft && data.secondsLeft) {
            console.log(`⚠️ Warning received: ${data.secondsLeft} seconds (${data.minutesLeft} minutes) left`);
            setState(prev => {
              // Only update if this is the first warning or if server time is very different
              const shouldUpdate = !prev.showWarning || Math.abs(prev.secondsLeft - data.secondsLeft!) > 15;

              if (shouldUpdate) {
                console.log(`🔄 Updating countdown to ${data.secondsLeft} seconds`);
                return {
                  ...prev,
                  showWarning: true,
                  minutesLeft: data.minutesLeft!,
                  secondsLeft: data.secondsLeft!
                };
              } else {
                console.log(`⏰ Keeping current countdown at ${prev.secondsLeft} seconds (server: ${data.secondsLeft})`);
                return { ...prev, showWarning: true };
              }
            });
          } else if (!data.isNearTimeout) {
            // Hide warning if we're no longer near timeout
            setState(prev => ({ ...prev, showWarning: false }));
          }
        }
      } catch (error: any) {
        // Only log non-abortion errors
        if (error.name !== 'AbortError') {
          console.error('Timeout check error:', error);
        }
      }
    };

    // Smart polling intervals based on state
    let currentInterval: ReturnType<typeof setInterval> | null = null;
    let currentTimeout: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextCheck = (isWarning: boolean = false) => {
      // More frequent when warning is active (5s), less when idle (30s)
      const interval = isWarning ? 5000 : 30000;

      currentTimeout = setTimeout(() => {
        checkTimeout().then(() => {
          // Re-schedule based on current warning state
          scheduleNextCheck(state.showWarning);
        });
      }, interval);
    };

    // Initial check after 5 minutes (to catch existing timeouts without being intrusive)
    currentTimeout = setTimeout(() => {
      checkTimeout().then(() => {
        // Start smart polling after initial check
        scheduleNextCheck(state.showWarning);
      });
    }, 5 * 60 * 1000);

    return () => {
      // Cleanup: abort pending requests and clear timers
      controller.abort();
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      if (currentInterval) {
        clearInterval(currentInterval);
      }
      console.log('🧹 Cleaned up idle timeout hook - aborted requests and cleared timers');
    };
  }, [user?.id]);

  return {
    ...state,
    extendSession,
    dismissWarning,
  };
}